import { useEffect, useState } from 'react';
import { getStudentByUserIdQuery } from '../../db/api/get-student.js';
import { getTeacherByUserIdQuery } from '../../db/api/get-teacher.js';
import type { Student } from '../../db/student.js';
import type { Teacher } from '../../db/teacher.js';
import { useCurrentUser } from './useCurrentUser.js';

export type UserRole = 'teacher' | 'student';

const PROFILE_SELECTION = {
  firstName: 1,
  lastName: 1,
  school: 1,
  subjects: 1,
  email: 1,
  language: 1,
} as const;

/**
 * Resolves whether the current user is a teacher or a student by checking which profile exists for
 * their userID (Teacher and Student are otherwise independent, sibling entities — see [[db/teacher.ts]]
 * and [[db/student.ts]]). Existing accounts only ever have a Teacher profile, so they resolve to
 * 'teacher' automatically without any migration.
 */
export function useCurrentUserProfile() {
  const { user, isLoading: isUserLoading } = useCurrentUser();
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [student, setStudent] = useState<Student | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  useEffect(() => {
    const userId = (user as any)?._id;
    if (!userId) {
      setTeacher(null);
      setStudent(null);
      setIsProfileLoading(false);
      return;
    }

    let isMounted = true;
    setIsProfileLoading(true);

    void Promise.all([
      getTeacherByUserIdQuery(userId)(PROFILE_SELECTION).catch(() => []),
      getStudentByUserIdQuery(userId)(PROFILE_SELECTION).catch(() => []),
    ]).then(([teachers, students]) => {
      if (!isMounted) return;
      setTeacher(teachers?.[0] ?? null);
      setStudent(students?.[0] ?? null);
      setIsProfileLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [user]);

  const role: UserRole | undefined = teacher ? 'teacher' : student ? 'student' : undefined;

  return {
    role,
    teacher,
    student,
    isLoading: isUserLoading || isProfileLoading,
  };
}
